<template>
  <section class="dynamic-venue" :class="{ 'has-selection': hasSelection, 'is-section-focused': activeSection, 'has-zone-picker': zonePickerSection }">
    <div v-if="loading" class="dynamic-venue__loading">
      <div class="dynamic-venue__loader-ring"></div>
      <strong>Armando el venue...</strong>
      <small>Cargando zonas, etapas y disponibilidad.</small>
    </div>

    <div v-else-if="loadError" class="dynamic-venue__error">
      <strong>No pudimos abrir el Venue Dinámico</strong>
      <p>{{ loadError }}</p>
      <button class="dynamic-venue__primary" type="button" @click="$emit('fallback')">Usar checkout normal</button>
    </div>

    <div v-else class="dynamic-venue__grid">
      <div class="dynamic-venue__map-card">
        <div class="dynamic-venue__map-toolbar">
          <div class="dynamic-venue__breadcrumb">
            <button type="button" @click="goOverview">Vista general</button>
            <span v-if="activeSection">/ {{ activeSection.name }}</span>
            <small>{{ activeSection ? activeSection.description : 'Toca una zona, haz zoom y elige asiento exacto.' }}</small>
          </div>
          <div class="dynamic-venue__legend">
            <span><i class="is-available"></i>Disponible</span>
            <span><i class="is-selected"></i>Seleccionado</span>
            <span><i class="is-occupied"></i>No disponible</span>
          </div>
        </div>

        <div class="dynamic-venue__zone-strip" aria-label="Localidades del venue">
          <button
            v-for="section in sections"
            :key="`strip-${section.id}`"
            type="button"
            :class="{ 'is-active': activeSection && activeSection.id === section.id }"
            :disabled="!section.is_configured"
            @click="handleSectionClick(section)"
          >
            <span>{{ section.name }}</span>
            <strong>{{ section.ticket ? formatMoney(ticketGrossAmount(section.ticket)) : 'No disponible' }}</strong>
          </button>
        </div>

        <div class="dynamic-venue__stage" :class="{ 'is-dragging': isDragging, 'is-pinching': isPinching, 'is-section-focused': activeSection, 'has-selection': hasSelection }">
          <svg
            ref="venueSvg"
            class="dynamic-venue__svg"
            :viewBox="viewBoxString"
            role="img"
            aria-label="Mapa dinámico de asientos"
            @pointerdown="beginMapInteraction"
            @pointermove="moveMapInteraction"
            @pointerup="endMapInteraction"
            @pointercancel="endMapInteraction"
            @wheel.prevent="handleWheelZoom"
            @dblclick.prevent="handleDoubleClickZoom"
          >
            <defs>
              <filter id="dynamicVenueGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <linearGradient id="dynamicVenueRing" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stop-color="#f4f8ff" />
                <stop offset="46%" stop-color="#dce5fb" />
                <stop offset="100%" stop-color="#8e6bc4" />
              </linearGradient>
              <linearGradient id="dynamicVenueSeatAvailable" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stop-color="#f7fbff" />
                <stop offset="100%" stop-color="#aebbd0" />
              </linearGradient>
              <linearGradient id="dynamicVenueSeatSelected" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stop-color="#f8f5ff" />
                <stop offset="52%" stop-color="#b19de4" />
                <stop offset="100%" stop-color="#7054b8" />
              </linearGradient>
              <pattern id="dynamicVenueGrid" width="90" height="90" patternUnits="userSpaceOnUse">
                <path d="M 90 0 L 0 0 0 90" fill="none" stroke="rgba(200, 220, 245, 0.09)" stroke-width="1" />
              </pattern>
            </defs>

            <g class="dynamic-venue__viewport" :transform="pinchPreviewTransform || null">
              <rect class="dynamic-venue__floor" x="0" y="-70" width="1620" height="1290" rx="28" />
              <rect class="dynamic-venue__floor-grid" x="0" y="-70" width="1620" height="1290" rx="28" />

              <g class="dynamic-venue__guides" aria-hidden="true">
                <line x1="810" y1="-40" x2="810" y2="1190" />
                <line x1="60" y1="575" x2="1560" y2="575" />
                <circle cx="810" cy="605" r="390" />
                <circle cx="810" cy="605" r="560" />
              </g>

              <g class="dynamic-venue__zones">
                <g
                  v-for="section in sections"
                  :key="section.id"
                  class="dynamic-venue__zone"
                  :class="{ 'is-active': activeSection && activeSection.id === section.id, 'is-muted': activeSection && activeSection.id !== section.id, 'is-unconfigured': !section.is_configured, 'has-zone-selection': isZoneSection(section) && zoneQuantity(section) > 0 }"
                  @pointerdown="beginSectionTap(section)"
                >
                  <rect
                    class="dynamic-venue__zone-aura"
                    :x="section.shape.x - 8"
                    :y="section.shape.y - 8"
                    :width="section.shape.w + 16"
                    :height="section.shape.h + 16"
                    :rx="(section.shape.rx || 28) + 8"
                    :stroke="sectionTone(section.id)"
                  />
                  <rect
                    :x="section.shape.x"
                    :y="section.shape.y"
                    :width="section.shape.w"
                    :height="section.shape.h"
                    :rx="section.shape.rx || 28"
                    :fill="sectionFill(section.id)"
                    :stroke="sectionTone(section.id)"
                  />
                  <text v-if="shouldShowSectionLabel(section)" :x="section.shape.x + section.shape.w / 2" :y="section.shape.y + 42" text-anchor="middle">
                    {{ section.name }}
                  </text>
                  <text v-if="shouldShowSectionLabel(section)" class="dynamic-venue__zone-price" :x="section.shape.x + section.shape.w / 2" :y="section.shape.y + 70" text-anchor="middle">
                    {{ section.ticket ? formatMoney(ticketGrossAmount(section.ticket)) : 'Configurar etapa' }}
                  </text>
                </g>
              </g>

              <g v-for="object in objects" :key="object.id" class="dynamic-venue__object">
                <g v-if="object.type === 'ring'" class="dynamic-venue__object-ring">
                  <rect class="dynamic-venue__object-ring-shadow" :x="object.x - 18" :y="object.y - 18" :width="object.w + 36" :height="object.h + 36" rx="26" />
                  <rect class="dynamic-venue__object-ring-floor" :x="object.x" :y="object.y" :width="object.w" :height="object.h" rx="18" />
                  <rect class="dynamic-venue__object-ring-inner" :x="object.x + 18" :y="object.y + 18" :width="object.w - 36" :height="object.h - 36" rx="12" />
                  <line :x1="object.x + 26" :x2="object.x + object.w - 26" :y1="object.y + 54" :y2="object.y + 54" />
                  <line :x1="object.x + 26" :x2="object.x + object.w - 26" :y1="object.y + object.h - 54" :y2="object.y + object.h - 54" />
                  <line :x1="object.x + 54" :x2="object.x + 54" :y1="object.y + 26" :y2="object.y + object.h - 26" />
                  <line :x1="object.x + object.w - 54" :x2="object.x + object.w - 54" :y1="object.y + 26" :y2="object.y + object.h - 26" />
                </g>
                <rect v-else class="dynamic-venue__object-entrance" :x="object.x" :y="object.y" :width="object.w" :height="object.h" rx="14" />
                <text :x="object.x + object.w / 2" :y="object.y + object.h / 2 + 8" text-anchor="middle">{{ object.label }}</text>
              </g>

              <g class="dynamic-venue__seats">
                <g
                  v-for="seat in visibleSeats"
                  :key="seat.key"
                  class="dynamic-venue__seat"
                  :class="seatClass(seat)"
                  :transform="seatTransform(seat)"
                  @pointerdown.stop
                  @pointerup.stop="handleSeatClick(seat)"
                  @mouseenter="hoverSeat(seat)"
                  @mouseleave="hoverSeat(null)"
                >
                  <rect class="dynamic-venue__seat-hit" :x="-8" :y="-8" :width="seat.w + 16" :height="seat.h + 16" rx="12" />
                  <rect v-if="isSeatSelected(seat)" class="dynamic-venue__seat-halo" :x="-6" :y="-6" :width="seat.w + 12" :height="seat.h + 12" rx="12" />
                  <rect :width="seat.w" :height="seat.h" rx="7" />
                  <text v-if="shouldShowSeatLabel(seat)" :x="seat.w / 2" :y="seat.h / 2 + 4" text-anchor="middle">{{ seat.label }}</text>
                </g>
              </g>
            </g>
          </svg>

          <div class="dynamic-venue__zoom-controls" aria-label="Controles de mapa">
            <button type="button" @pointerdown.stop @click.stop="zoomIn"><i class="fas fa-plus"></i></button>
            <button type="button" @pointerdown.stop @click.stop="zoomOut"><i class="fas fa-minus"></i></button>
            <button type="button" @pointerdown.stop @click.stop="resetCamera"><i class="fas fa-expand"></i></button>
          </div>

          <div v-if="previewSeatInfo" class="dynamic-venue__seat-peek">
            <small>{{ previewSeatInfo.selected ? 'Asiento reservado' : 'Vista previa' }}</small>
            <strong>{{ previewSeatInfo.seat.seat_name || previewSeatInfo.seat.label }}</strong>
            <span>{{ previewSeatInfo.ticket ? previewSeatInfo.ticket.name : previewSeatInfo.section?.name }} · {{ previewSeatInfo.ticket ? formatMoney(ticketGrossAmount(previewSeatInfo.ticket)) : 'No disponible' }}</span>
          </div>

          <div v-if="zonePickerSection" class="dynamic-venue__zone-picker" role="group" aria-live="polite">
            <small>Elige cantidad para esta zona</small>
            <div class="dynamic-venue__zone-picker-title">
              <strong>{{ zonePickerSection.name }}</strong>
              <span v-if="zoneQuantity(zonePickerSection) > 0">x{{ zoneQuantity(zonePickerSection) }}</span>
            </div>
            <span>Zona visual sin asiento numerado · {{ zonePickerSection.ticket ? formatMoney(ticketGrossAmount(zonePickerSection.ticket)) : 'No disponible' }} por acceso</span>
            <div class="dynamic-venue__zone-stepper">
              <button type="button" :aria-label="`Restar acceso de ${zonePickerSection.name}`" @pointerdown.stop @click.stop="decrementZone(zonePickerSection)"><i class="fas fa-minus"></i></button>
              <b aria-live="polite">{{ zoneQuantity(zonePickerSection) }}</b>
              <button type="button" :aria-label="`Sumar acceso de ${zonePickerSection.name}`" @pointerdown.stop @click.stop="incrementZone(zonePickerSection)"><i class="fas fa-plus"></i></button>
            </div>
          </div>

          <p v-if="holdError" class="dynamic-venue__hold-toast">
            <i class="fas fa-circle-exclamation"></i>
            <span>{{ holdError }}</span>
          </p>

          <div v-if="!hasSelection" class="dynamic-venue__map-hint">
            <i class="fas fa-crosshairs"></i>
            <span>{{ mapGuidance }}</span>
          </div>

          <div v-if="hasSelection" class="dynamic-venue__mobile-cta">
            <div>
              <small>{{ selectedSelectionSummary }}</small>
              <strong>{{ formatMoney(subtotal) }}</strong>
              <small v-if="taxes > 0" class="dynamic-venue__mobile-tax">Subtotal {{ formatMoney(subtotalBase) }} · {{ taxSummaryLabel }} {{ formatMoney(taxes) }}</small>
            </div>
            <button type="button" :disabled="holding" @pointerdown.stop @click.stop="continueCheckout">
              <span>Continuar</span>
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      <aside class="dynamic-venue__summary" :class="{ 'is-empty': !hasSelection }">
        <div class="dynamic-venue__summary-head">
          <small>Resumen</small>
          <strong>{{ hasSelection ? `${selectionCount} acceso${selectionCount === 1 ? '' : 's'}` : 'Sin accesos' }}</strong>
          <p>{{ hasSelection ? 'Tus lugares están reservados temporalmente mientras avanzas.' : 'Elige una zona del mapa para comprar.' }}</p>
        </div>

        <div v-if="selectedSeats.length" class="dynamic-venue__selected-list">
          <article v-for="item in selectedSeats" :key="item.seat.key" class="dynamic-venue__selected-item">
            <div class="dynamic-venue__selected-mark">
              <span>{{ item.seat.seat_name || item.seat.label }}</span>
            </div>
            <div class="dynamic-venue__selected-copy">
              <small>{{ item.ticket.name }}</small>
              <strong>{{ sectionNameForSeat(item.seat) || item.ticket.name }}</strong>
              <em>Reserva protegida hasta avanzar al pago</em>
            </div>
            <span>{{ formatMoney(ticketGrossAmount(item.ticket)) }}</span>
            <button type="button" @click="releaseSeat(item)"><i class="fas fa-trash"></i></button>
          </article>
        </div>

        <div v-if="selectedZones.length" class="dynamic-venue__selected-list">
          <article v-for="item in selectedZones" :key="item.section.id" class="dynamic-venue__selected-item dynamic-venue__selected-item--zone">
            <div class="dynamic-venue__selected-mark">
              <span>{{ item.quantity }}x</span>
            </div>
            <div class="dynamic-venue__selected-copy">
              <small>{{ item.ticket.name }}</small>
              <strong>{{ item.section.name }}</strong>
              <em>Zona visual sin asiento numerado</em>
            </div>
            <span>{{ formatMoney(ticketGrossAmount(item.ticket) * item.quantity) }}</span>
            <button type="button" @click="removeZone(item.section)"><i class="fas fa-trash"></i></button>
          </article>
        </div>

        <div v-if="!hasSelection" class="dynamic-venue__empty-box">
          <i class="fas fa-map-location-dot"></i>
          <strong>Tu mapa está listo</strong>
          <span>Selecciona una zona o asiento y el resumen aparecerá aquí.</span>
        </div>

        <p v-if="holdError" class="dynamic-venue__hold-error">{{ holdError }}</p>

        <div class="dynamic-venue__totals">
          <div><span>Subtotal</span><strong>{{ formatMoney(subtotalBase) }}</strong></div>
          <div v-if="taxes > 0"><span>{{ taxSummaryLabel }}</span><strong>{{ formatMoney(taxes) }}</strong></div>
        </div>

        <button class="dynamic-venue__primary" type="button" :disabled="!hasSelection || holding" @click="continueCheckout">
          <span>{{ holding ? 'Reservando...' : (hasSelection ? `Continuar · ${formatMoney(subtotal)}` : 'Elige una zona') }}</span>
          <i class="fas fa-arrow-right"></i>
        </button>
      </aside>
    </div>
  </section>
</template>

<script>
import { apiServiceSession } from '../../common/api.services.js'
import { ticketUnitPricing } from '../../common/ticketTaxes.js'
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

export default {
  name: 'DynamicVenueMap',
  props: {
    event: { type: Object, required: true },
    code: { type: String, default: '' },
  },
  emits: ['messageFromDynamicVenue', 'fallback'],
  data() {
    return {
      loading: true,
      loadError: '',
      payload: null,
      layout: null,
      activeSectionId: '',
      selectedSeats: [],
      selectedZones: [],
      holdError: '',
      holding: false,
      customerSession: '',
      camera: { x: 0, y: -70, width: 1620, height: 1290 },
      animationFrame: null,
      cameraFrame: null,
      pendingCamera: null,
      svgRect: null,
      isPinching: false,
      pinchStartCamera: null,
      pinchStartDistance: 0,
      pinchStartMidpoint: null,
      pinchStartAnchor: null,
      pinchPreview: null,
      pinchPreviewTransform: '',
      hoveredSeatKey: '',
      isDragging: false,
      dragMoved: false,
      suppressNextClick: false,
      pointers: {},
      lastPointer: null,
      lastPinchDistance: 0,
      pendingSectionTapId: '',
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1440,
    }
  },
  computed: {
    sections() {
      return (this.layout && Array.isArray(this.layout.sections) ? this.layout.sections : []).map((section) => ({
        ...section,
        shape: section.shape || this.fallbackSectionShape(section.id),
      })).sort((first, second) => {
        const firstArea = (first.shape?.w || 0) * (first.shape?.h || 0)
        const secondArea = (second.shape?.w || 0) * (second.shape?.h || 0)
        return secondArea - firstArea
      })
    },
    objects() {
      return this.layout && Array.isArray(this.layout.objects) ? this.layout.objects : []
    },
    seats() {
      return this.layout && Array.isArray(this.layout.seats) ? this.layout.seats : []
    },
    activeSection() {
      return this.sections.find((section) => section.id === this.activeSectionId) || null
    },
    isCompactViewport() {
      return this.viewportWidth <= 720
    },
    mapGuidance() {
      if (this.activeSection && this.isZoneSection(this.activeSection)) return 'Ajusta la cantidad para esta zona visual sin asiento numerado.'
      if (this.activeSection) return 'Mueve el mapa con un dedo, pellizca para acercarte y toca un asiento.'
      return 'Toca una zona: algunas tienen asiento exacto y otras se compran por cantidad.'
    },
    visibleSeats() {
      let result = []
      if (!this.activeSection) {
        const seatSectionIds = this.sections.filter((section) => this.isSeatSection(section)).map((section) => section.id)
        result = this.seats.filter((seat) => seatSectionIds.includes(seat.section_id))
      } else {
        result = this.isSeatSection(this.activeSection)
          ? this.seats.filter((seat) => seat.section_id === this.activeSection.id || this.isSeatSelected(seat))
          : this.seats.filter((seat) => this.isSeatSelected(seat))
      }
      return result.slice().sort((left, right) => Number(this.isSeatSelected(left)) - Number(this.isSeatSelected(right)))
    },
    hasSelection() {
      return Boolean(this.selectedSeats.length || this.selectedZones.length)
    },
    selectionCount() {
      return this.selectedSeats.length + this.selectedZones.reduce((sum, item) => sum + item.quantity, 0)
    },
    zonePickerSection() {
      return this.activeSection && this.isZoneSection(this.activeSection) && this.activeSection.ticket ? this.activeSection : null
    },
    viewBoxString() {
      const c = this.camera
      return `${c.x} ${c.y} ${c.width} ${c.height}`
    },
    subtotal() {
      const seatTotal = this.selectedSeats.reduce((sum, item) => sum + this.ticketGrossAmount(item.ticket), 0)
      const zoneTotal = this.selectedZones.reduce((sum, item) => sum + (this.ticketGrossAmount(item.ticket) * item.quantity), 0)
      return seatTotal + zoneTotal
    },
    taxes() {
      const seatTaxes = this.selectedSeats.reduce((sum, item) => sum + this.ticketTaxAmount(item.ticket), 0)
      const zoneTaxes = this.selectedZones.reduce((sum, item) => sum + (this.ticketTaxAmount(item.ticket) * item.quantity), 0)
      return seatTaxes + zoneTaxes
    },
    subtotalBase() {
      return Math.max(this.subtotal - this.taxes, 0)
    },
    taxSummaryLabel() {
      const labels = [...this.selectedSeats, ...this.selectedZones]
        .filter((item) => this.ticketTaxAmount(item.ticket) > 0)
        .map((item) => item.ticket.tax_label || 'IVA')
      const uniqueLabels = [...new Set(labels)]
      return uniqueLabels.length === 1 ? uniqueLabels[0] : 'Impuestos'
    },
    activePricingProfile() {
      const options = Array.isArray(this.event.payment_options) ? this.event.payment_options : []
      return options[0] && options[0].pricing_profile ? options[0].pricing_profile : null
    },
    fee() {
      if (!this.subtotal) return 0
      const profile = this.activePricingProfile
      const percent = Number.parseFloat(profile?.platform_fee_percent ?? this.event.coordinated_fee) || 0
      const perTicket = Number.parseFloat(profile?.platform_fee_per_ticket ?? this.event.coordinated_service) || 0
      const fixed = Number.parseFloat(profile?.platform_fee_fixed ?? this.event.fixed_fee) || 0
      return (this.subtotal * percent) + (perTicket * this.selectionCount) + fixed
    },
    total() {
      return this.subtotal + this.fee
    },
    selectedSeatSummary() {
      if (!this.selectedSeats.length) return 'Sin asientos'
      if (this.selectedSeats.length === 1) {
        const item = this.selectedSeats[0]
        return `${item.seat.seat_name || item.seat.label} reservado`
      }
      return `${this.selectedSeats.length} asientos reservados`
    },
    selectedSelectionSummary() {
      if (this.selectedSeats.length) return this.selectedSeatSummary
      if (this.selectedZones.length === 1) {
        const item = this.selectedZones[0]
        return `${item.section.name} x${item.quantity}`
      }
      return `${this.selectionCount} accesos seleccionados`
    },
    currency() {
      return normalizeCurrencyCode(this.event.presentment_currency || 'USD', 'USD')
    },
    previewSeatInfo() {
      const selected = this.selectedSeats[this.selectedSeats.length - 1]
      if (selected) {
        return {
          seat: selected.seat,
          ticket: selected.ticket,
          section: this.sectionForSeat(selected.seat),
          selected: true,
        }
      }

      const hovered = this.seats.find((seat) => seat.key === this.hoveredSeatKey)
      if (!hovered) return null
      return {
        seat: hovered,
        ticket: this.ticketForSeat(hovered),
        section: this.sectionForSeat(hovered),
        selected: false,
      }
    },
  },
  created() {
    this.customerSession = this.resolveCustomerSession()
    this.fetchVenueMap()
  },
  mounted() {
    this.syncViewportWidth()
    window.addEventListener('resize', this.syncViewportWidth, { passive: true })
  },
  beforeUnmount() {
    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame)
    if (this.cameraFrame) window.cancelAnimationFrame(this.cameraFrame)
    window.removeEventListener('resize', this.syncViewportWidth)
  },
  methods: {
    syncViewportWidth() {
      this.viewportWidth = window.innerWidth || this.viewportWidth
    },
    resolveCustomerSession() {
      const key = 'fomo_dynamic_venue_session'
      const existing = window.sessionStorage.getItem(key)
      if (existing) return existing
      const next = `venue-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
      window.sessionStorage.setItem(key, next)
      return next
    },
    eventLookup() {
      return this.event.slug || this.event.id
    },
    fetchVenueMap() {
      const slug = this.eventLookup()
      const codeQuery = this.code ? `?code=${encodeURIComponent(this.code)}` : ''
      this.loading = true
      this.loadError = ''
      apiServiceSession(`/api/events/${slug}/venue-map/${codeQuery}`, undefined, undefined, { timeoutMs: 20000 }).then((data) => {
        if (!data || data.err || data.enabled === false) {
          this.loadError = data && (data.err || data.reason) ? (data.err || data.reason) : 'El mapa dinámico no está disponible.'
          return
        }
        this.payload = data
        this.layout = data.layout
        const viewBox = this.layout.view_box || { x: 0, y: -70, width: 1620, height: 1290 }
        this.camera = { ...viewBox }
      }).finally(() => {
        this.loading = false
      })
    },
    fallbackSectionShape(sectionId) {
      const shapes = {
        general: { x: 30, y: -55, w: 1560, h: 1230, rx: 34 },
        'ring-side-regular': { x: 280, y: 155, w: 1060, h: 890, rx: 28 },
        'ring-side-vip': { x: 465, y: 330, w: 690, h: 500, rx: 24 },
      }
      return shapes[sectionId] || { x: 80, y: 80, w: 400, h: 240, rx: 24 }
    },
    shouldShowSectionLabel(section) {
      return !this.activeSection && section
    },
    formatMoney(value) {
      return formatCurrencyAmount(value, this.currency)
    },
    ticketPricing(ticket) {
      return ticketUnitPricing(ticket)
    },
    ticketGrossAmount(ticket) {
      return this.ticketPricing(ticket).grossAmount
    },
    ticketTaxAmount(ticket) {
      return this.ticketPricing(ticket).taxAmount
    },
    ticketPayload(ticket) {
      const pricing = this.ticketPricing(ticket)
      return {
        amount: pricing.grossAmount,
        base_amount: pricing.baseAmount,
        tax_amount: pricing.taxAmount,
        tax_label: pricing.taxLabel,
        tax_percent: ticket.tax_percent,
        tax_enabled: Boolean(ticket.tax_enabled),
        tax_price_includes_tax: pricing.priceIncludesTax,
      }
    },
    baseViewBox() {
      return this.layout?.view_box || { x: 0, y: -70, width: 1620, height: 1290 }
    },
    activeCamera() {
      return this.pendingCamera || this.camera
    },
    formatTransformNumber(value) {
      const number = Number(value) || 0
      return Number(number.toFixed(4))
    },
    cacheSvgRect() {
      const rect = this.$refs.venueSvg?.getBoundingClientRect()
      this.svgRect = rect && rect.width && rect.height ? {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      } : null
      return this.svgRect
    },
    flushPendingCamera() {
      if (this.cameraFrame) {
        window.cancelAnimationFrame(this.cameraFrame)
        this.cameraFrame = null
      }
      if (this.pendingCamera) {
        this.camera = this.pendingCamera
        this.pendingCamera = null
      }
    },
    scheduleCamera(camera, immediate = false) {
      const nextCamera = this.clampCamera(camera)
      if (immediate || typeof window === 'undefined') {
        this.flushPendingCamera()
        this.camera = nextCamera
        return
      }

      this.pendingCamera = nextCamera
      if (this.cameraFrame) return

      this.cameraFrame = window.requestAnimationFrame(() => {
        this.cameraFrame = null
        if (!this.pendingCamera) return
        this.camera = this.pendingCamera
        this.pendingCamera = null
      })
    },
    resetPinchPreview() {
      this.isPinching = false
      this.pinchStartCamera = null
      this.pinchStartDistance = 0
      this.pinchStartMidpoint = null
      this.pinchStartAnchor = null
      this.pinchPreview = null
      this.pinchPreviewTransform = ''
    },
    startPinchPreview(points) {
      if (!Array.isArray(points) || points.length < 2) return
      this.flushPendingCamera()
      const camera = { ...this.camera }
      const rect = this.svgRect || this.cacheSvgRect()
      const distance = this.pointerDistance(points)
      if (!rect || distance <= 0) return
      const midpoint = this.pointerMidpoint(points)
      const anchor = this.clientToViewBox(midpoint.x, midpoint.y, camera)
      if (!anchor) return

      this.isPinching = true
      this.pinchStartCamera = camera
      this.pinchStartDistance = distance
      this.pinchStartMidpoint = midpoint
      this.pinchStartAnchor = anchor
      this.pinchPreview = null
      this.pinchPreviewTransform = ''
      this.lastPinchDistance = distance
    },
    updatePinchPreview(points) {
      if (!Array.isArray(points) || points.length < 2) return
      if (!this.isPinching || !this.pinchStartCamera || !this.pinchStartDistance || !this.pinchStartMidpoint || !this.pinchStartAnchor) {
        this.startPinchPreview(points)
      }
      if (!this.isPinching || !this.pinchStartCamera || !this.pinchStartDistance || !this.pinchStartMidpoint || !this.pinchStartAnchor) return

      const rect = this.svgRect || this.cacheSvgRect()
      if (!rect) return

      const distance = this.pointerDistance(points)
      if (distance <= 0) return

      const camera = this.pinchStartCamera
      const base = this.baseViewBox()
      const minWidth = base.width * 0.2
      const maxWidth = base.width * 1.06
      const minScale = camera.width / maxWidth
      const maxScale = camera.width / minWidth
      const rawScale = distance / this.pinchStartDistance
      const scale = Math.max(minScale, Math.min(maxScale, rawScale))
      const midpoint = this.pointerMidpoint(points)
      const dx = (midpoint.x - this.pinchStartMidpoint.x) * (camera.width / rect.width)
      const dy = (midpoint.y - this.pinchStartMidpoint.y) * (camera.height / rect.height)
      const translateX = dx + (this.pinchStartAnchor.x * (1 - scale))
      const translateY = dy + (this.pinchStartAnchor.y * (1 - scale))

      this.pinchPreview = { scale, translateX, translateY }
      this.pinchPreviewTransform = `matrix(${this.formatTransformNumber(scale)} 0 0 ${this.formatTransformNumber(scale)} ${this.formatTransformNumber(translateX)} ${this.formatTransformNumber(translateY)})`
      this.dragMoved = true
    },
    commitPinchPreview() {
      if (!this.isPinching) return
      const camera = this.pinchStartCamera
      const preview = this.pinchPreview
      if (camera && preview && preview.scale > 0) {
        this.scheduleCamera({
          x: (camera.x - preview.translateX) / preview.scale,
          y: (camera.y - preview.translateY) / preview.scale,
          width: camera.width / preview.scale,
          height: camera.height / preview.scale,
        }, true)
      }
      this.resetPinchPreview()
    },
    sectionTone(sectionId) {
      const tones = {
        general: '#7e91b8',
        'ring-side-regular': '#8e6bc4',
        'ring-side-vip': '#dce5fb',
      }
      return tones[sectionId] || '#cad6df'
    },
    sectionFill(sectionId) {
      const fills = {
        general: 'rgba(126, 145, 184, 0.16)',
        'ring-side-regular': 'rgba(142, 107, 196, 0.18)',
        'ring-side-vip': 'rgba(220, 229, 251, 0.18)',
      }
      return fills[sectionId] || 'rgba(202, 214, 223, 0.16)'
    },
    clampCamera(camera) {
      const base = this.baseViewBox()
      const minWidth = base.width * 0.2
      const maxWidth = base.width * 1.06
      const nextWidth = Math.max(minWidth, Math.min(maxWidth, camera.width))
      const ratio = camera.height / camera.width || this.camera.height / this.camera.width || (base.height / base.width)
      const nextHeight = nextWidth * ratio
      const bleedX = base.width * 0.08
      const bleedY = base.height * 0.08
      let nextX = camera.x
      let nextY = camera.y
      const minX = base.x - bleedX
      const maxX = base.x + base.width - nextWidth + bleedX
      const minY = base.y - bleedY
      const maxY = base.y + base.height - nextHeight + bleedY

      if (minX > maxX) nextX = base.x + ((base.width - nextWidth) / 2)
      else nextX = Math.max(minX, Math.min(maxX, nextX))

      if (minY > maxY) nextY = base.y + ((base.height - nextHeight) / 2)
      else nextY = Math.max(minY, Math.min(maxY, nextY))

      return { x: nextX, y: nextY, width: nextWidth, height: nextHeight }
    },
    focusSection(section) {
      this.activeSectionId = section.id
      this.animateCamera(section.view_box || this.fallbackSectionShape(section.id))
    },
    goOverview() {
      this.activeSectionId = ''
      this.animateCamera(this.layout.view_box || { x: 0, y: -70, width: 1620, height: 1290 })
    },
    animateCamera(target) {
      this.flushPendingCamera()
      const from = { ...this.camera }
      const to = { x: target.x, y: target.y, width: target.width || target.w, height: target.height || target.h }
      const start = performance.now()
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame)
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / 520)
        const eased = easeOutCubic(progress)
        this.camera = {
          x: from.x + ((to.x - from.x) * eased),
          y: from.y + ((to.y - from.y) * eased),
          width: from.width + ((to.width - from.width) * eased),
          height: from.height + ((to.height - from.height) * eased),
        }
        if (progress < 1) this.animationFrame = window.requestAnimationFrame(tick)
      }
      this.animationFrame = window.requestAnimationFrame(tick)
    },
    clientToViewBox(clientX, clientY, camera = this.activeCamera()) {
      const rect = this.svgRect || this.cacheSvgRect()
      if (!rect || rect.width === 0 || rect.height === 0) return null
      const xRatio = (clientX - rect.left) / rect.width
      const yRatio = (clientY - rect.top) / rect.height
      return {
        x: camera.x + (xRatio * camera.width),
        y: camera.y + (yRatio * camera.height),
      }
    },
    zoomCamera(factor, clientX, clientY) {
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame)
      const camera = this.activeCamera()
      const anchor = this.clientToViewBox(clientX, clientY, camera) || {
        x: camera.x + (camera.width / 2),
        y: camera.y + (camera.height / 2),
      }
      const nextWidth = camera.width * factor
      const nextHeight = camera.height * factor
      const widthRatio = nextWidth / camera.width
      const heightRatio = nextHeight / camera.height
      this.scheduleCamera({
        x: anchor.x - ((anchor.x - camera.x) * widthRatio),
        y: anchor.y - ((anchor.y - camera.y) * heightRatio),
        width: nextWidth,
        height: nextHeight,
      }, !this.isDragging)
    },
    zoomIn() {
      const svg = this.$refs.venueSvg
      const rect = svg?.getBoundingClientRect()
      this.zoomCamera(0.78, rect ? rect.left + (rect.width / 2) : 0, rect ? rect.top + (rect.height / 2) : 0)
    },
    zoomOut() {
      const svg = this.$refs.venueSvg
      const rect = svg?.getBoundingClientRect()
      this.zoomCamera(1.22, rect ? rect.left + (rect.width / 2) : 0, rect ? rect.top + (rect.height / 2) : 0)
    },
    resetCamera() {
      this.goOverview()
    },
    handleWheelZoom(event) {
      this.zoomCamera(event.deltaY > 0 ? 1.12 : 0.88, event.clientX, event.clientY)
    },
    handleDoubleClickZoom(event) {
      this.zoomCamera(0.7, event.clientX, event.clientY)
    },
    pointerList() {
      return Object.values(this.pointers)
    },
    pointerDistance(points) {
      if (points.length < 2) return 0
      const dx = points[0].x - points[1].x
      const dy = points[0].y - points[1].y
      return Math.hypot(dx, dy)
    },
    pointerMidpoint(points) {
      return {
        x: (points[0].x + points[1].x) / 2,
        y: (points[0].y + points[1].y) / 2,
      }
    },
    beginMapInteraction(event) {
      if (event.button !== undefined && event.button !== 0) return
      if (event.target?.closest?.('.dynamic-venue__seat')) return
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame)
      this.flushPendingCamera()
      this.cacheSvgRect()
      this.$refs.venueSvg?.setPointerCapture?.(event.pointerId)
      this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY }
      const points = this.pointerList()
      this.isDragging = true
      this.dragMoved = false
      if (points.length === 1) {
        this.lastPointer = { x: event.clientX, y: event.clientY }
      }
      if (points.length === 2) {
        this.startPinchPreview(points)
      }
    },
    beginSectionTap(section) {
      this.pendingSectionTapId = section?.id || ''
    },
    moveMapInteraction(event) {
      if (!this.pointers[event.pointerId]) return
      this.pointers[event.pointerId] = { x: event.clientX, y: event.clientY }
      const points = this.pointerList()
      if (points.length >= 2) {
        this.updatePinchPreview(points)
        return
      }
      if (!this.lastPointer) return
      const rect = this.svgRect || this.cacheSvgRect()
      if (!rect) return
      const dx = event.clientX - this.lastPointer.x
      const dy = event.clientY - this.lastPointer.y
      if (Math.abs(dx) + Math.abs(dy) > 6) this.dragMoved = true
      this.lastPointer = { x: event.clientX, y: event.clientY }
      const camera = this.activeCamera()
      this.scheduleCamera({
        x: camera.x - (dx * (camera.width / rect.width)),
        y: camera.y - (dy * (camera.height / rect.height)),
        width: camera.width,
        height: camera.height,
      })
    },
    endMapInteraction(event) {
      const hadMultiplePointers = this.pointerList().length > 1
      if (this.pointers[event.pointerId]) {
        delete this.pointers[event.pointerId]
      }
      const points = this.pointerList()
      if (this.isPinching && points.length < 2) {
        this.commitPinchPreview()
      }
      this.isDragging = points.length > 0
      this.lastPointer = points[0] || null
      this.lastPinchDistance = points.length >= 2 ? this.pointerDistance(points) : 0
      if (points.length >= 2 && !this.isPinching) {
        this.startPinchPreview(points)
      }
      if (!this.isDragging) {
        this.flushPendingCamera()
        this.svgRect = null
      }
      if (!this.isDragging && !this.dragMoved && !hadMultiplePointers && this.pendingSectionTapId) {
        const section = this.sections.find((entry) => entry.id === this.pendingSectionTapId)
        if (section) this.handleSectionClick(section)
      }
      if (!this.isDragging) {
        this.pendingSectionTapId = ''
      }
      if (!this.isDragging && this.dragMoved) {
        this.suppressNextClick = true
        window.setTimeout(() => { this.suppressNextClick = false }, 140)
      }
    },
    handleSectionClick(section) {
      if (this.suppressNextClick) return
      if (!section.is_configured) {
        this.holdError = 'Esta zona todavía no tiene etapa disponible.'
        return
      }
      this.focusSection(section)
      if (this.isZoneSection(section) && this.zoneQuantity(section) === 0) {
        this.incrementZone(section)
      }
    },
    isSeatSection(section) {
      return Boolean(section && (section.selection_mode || section.kind) === 'seat')
    },
    isZoneSection(section) {
      return Boolean(section && (section.selection_mode || section.kind) === 'zone')
    },
    zoneQuantity(section) {
      const item = this.selectedZones.find((entry) => entry.section.id === section.id)
      return item ? item.quantity : 0
    },
    incrementZone(section) {
      if (!section || !section.ticket) return
      const current = this.selectedZones.find((entry) => entry.section.id === section.id)
      const max = Number.parseInt(section.ticket.max_quantity, 10) || 10
      if (current) {
        current.quantity = Math.min(max, current.quantity + 1)
        this.selectedZones = this.selectedZones.slice()
        return
      }
      this.selectedZones = this.selectedZones.concat({ section, ticket: section.ticket, quantity: 1 })
    },
    decrementZone(section) {
      if (!section) return
      const current = this.selectedZones.find((entry) => entry.section.id === section.id)
      if (!current) return
      if (current.quantity <= 1) {
        this.removeZone(section)
        return
      }
      current.quantity -= 1
      this.selectedZones = this.selectedZones.slice()
    },
    removeZone(section) {
      if (!section) return
      this.selectedZones = this.selectedZones.filter((entry) => entry.section.id !== section.id)
    },
    sectionForSeat(seat) {
      return this.sections.find((section) => section.id === seat.section_id) || null
    },
    sectionNameForSeat(seat) {
      const section = this.sectionForSeat(seat)
      return section ? section.name : ''
    },
    ticketForSeat(seat) {
      const section = this.sectionForSeat(seat)
      return section && section.ticket ? section.ticket : null
    },
    seatTransform(seat) {
      const rotation = Number.parseFloat(seat.rotation) || 0
      return `translate(${seat.x} ${seat.y}) rotate(${rotation} ${seat.w / 2} ${seat.h / 2})`
    },
    isSeatSelected(seat) {
      return this.selectedSeats.some((item) => item.seat.key === seat.key)
    },
    seatClass(seat) {
      return {
        'is-available': seat.state === 'available',
        'is-occupied': seat.state === 'occupied' || seat.state === 'held' || seat.state === 'unconfigured',
        'is-selected': this.isSeatSelected(seat),
        'is-muted': this.activeSection && seat.section_id !== this.activeSection.id && !this.isSeatSelected(seat),
      }
    },
    shouldShowSeatLabel(seat) {
      return Boolean(this.activeSection && seat.section_id === this.activeSection.id) || this.isSeatSelected(seat)
    },
    hoverSeat(seat) {
      this.hoveredSeatKey = seat ? seat.key : ''
    },
    handleSeatClick(seat) {
      if (this.suppressNextClick) return
      if (!this.activeSection || this.activeSection.id !== seat.section_id) {
        const section = this.sectionForSeat(seat)
        if (section && section.is_configured) this.focusSection(section)
      }
      this.toggleSeat(seat)
    },
    toggleSeat(seat) {
      if (this.holding) return
      const selected = this.selectedSeats.find((item) => item.seat.key === seat.key)
      if (selected) {
        this.releaseSeat(selected)
        return
      }
      this.reserveSeat(seat)
    },
    reserveSeat(seat) {
      if (this.holding) return
      const ticket = this.ticketForSeat(seat)
      if (!ticket || seat.state !== 'available') {
        this.holdError = 'Ese asiento no está disponible.'
        return
      }
      this.holding = true
      this.holdError = ''
      apiServiceSession(`/api/events/${this.eventLookup()}/venue-map/holds/`, 'POST', {
        seat_key: seat.key,
        code: this.code || '',
        customer_session: this.customerSession,
      }, { timeoutMs: 15000 }).then((data) => {
        if (!data || data.err || !data.hold_token) {
          this.holdError = data && data.err ? data.err : 'No pudimos reservar ese asiento.'
          return
        }
        this.selectedSeats = this.selectedSeats.concat({ seat: { ...seat, state: 'selected' }, ticket: data.ticket || ticket, hold: data })
      }).finally(() => {
        this.holding = false
      })
    },
    releaseSeat(item) {
      this.selectedSeats = this.selectedSeats.filter((selected) => selected.seat.key !== item.seat.key)
      if (item.hold && item.hold.hold_token) {
        apiServiceSession(`/api/events/${this.eventLookup()}/venue-map/holds/${item.hold.hold_token}/`, 'DELETE', undefined, { timeoutMs: 10000 })
      }
    },
    continueCheckout() {
      if (!this.hasSelection) return
      const grouped = []
      this.selectedSeats.forEach((item) => {
        let group = grouped.find((entry) => Number(entry.ticket.id) === Number(item.ticket.id))
        if (!group) {
          group = { ticket: item.ticket, seats: [] }
          grouped.push(group)
        }
        group.seats.push({ ...item.seat, hold_token: item.hold?.hold_token || '' })
      })

      const products = this.selectedZones.map((item) => ({
        remaining_tickets: item.ticket.remaining_tickets,
        host_remaining_tickets: item.ticket.host_remaining_tickets,
        quantity: item.quantity,
        ...this.ticketPayload(item.ticket),
        name: item.ticket.name,
        is_courtesy: item.ticket.is_courtesy,
        total: this.ticketGrossAmount(item.ticket) * item.quantity,
        id: item.ticket.id,
        sales_date: item.ticket.sales_date,
        expiration_date: item.ticket.expiration_date,
        quantity_factor: item.ticket.quantity || 1,
        map: false,
        max_quantity: item.ticket.max_quantity,
        description: item.ticket.description,
        generic_image: item.ticket.generic_image,
        guest_fields: Array.isArray(item.ticket.guest_fields) ? item.ticket.guest_fields : [],
        dynamic_venue_section_id: item.section.id,
        dynamic_venue_section_name: item.section.name,
      }))

      const seatsProducts = grouped.map((group) => ({
        remaining_tickets: group.ticket.remaining_tickets,
        host_remaining_tickets: group.ticket.host_remaining_tickets,
        quantity: group.seats.length,
        ...this.ticketPayload(group.ticket),
        name: group.ticket.name,
        is_courtesy: group.ticket.is_courtesy,
        total: this.ticketGrossAmount(group.ticket) * group.seats.length,
        id: group.ticket.id,
        sales_date: group.ticket.sales_date,
        expiration_date: group.ticket.expiration_date,
        quantity_factor: 1,
        map: true,
        max_quantity: group.ticket.max_quantity,
        description: group.ticket.description,
        generic_image: group.ticket.generic_image,
        guest_fields: Array.isArray(group.ticket.guest_fields) ? group.ticket.guest_fields : [],
      }))

      const selectedSeats = grouped.flatMap((group) => group.seats.map((seat) => ({
        id: seat.key,
        name: seat.seat_name || seat.label,
        hold_token: seat.hold_token || '',
      })))

      this.$emit('messageFromDynamicVenue', {
        products,
        seatsProducts,
        selectedSeats,
        subtotal: this.subtotal.toFixed(2),
        taxes: this.taxes.toFixed(2),
        fee: this.fee.toFixed(2),
        total: this.total.toFixed(2),
      })
    },
  },
}
</script>

<style scoped>
.dynamic-venue {
  --venue-accent: #dce5fb;
  --venue-accent-strong: #f4f7ff;
  --venue-violet: #8e6bc4;
  --venue-selection: #b19de4;
  --venue-selection-deep: #7054b8;
  --venue-selection-soft: #f8f5ff;
  --venue-blue: #95a8bb;
  --venue-ink: #05070d;
  --venue-panel: rgba(9, 13, 19, 0.86);
  --venue-stroke: rgba(196, 206, 228, 0.14);
  display: grid;
  gap: 18px;
  color: #f5f7fb;
}

.dynamic-venue__hero,
.dynamic-venue__map-card,
.dynamic-venue__summary,
.dynamic-venue__loading,
.dynamic-venue__error {
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    radial-gradient(circle at 10% 0%, rgba(220, 229, 251, 0.1), transparent 34%),
    radial-gradient(circle at 84% 12%, rgba(142, 107, 196, 0.18), transparent 32%),
    linear-gradient(145deg, rgba(13, 15, 20, 0.94), rgba(6, 8, 12, 0.98));
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.32);
  backdrop-filter: blur(24px) saturate(150%);
}

.dynamic-venue__hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  border-radius: 28px;
}

.dynamic-venue__hero small,
.dynamic-venue__summary small {
  color: var(--venue-accent);
  font-weight: 900;
  letter-spacing: 0.16em;
}

.dynamic-venue__hero h2 {
  margin: 4px 0;
  font-size: clamp(1.55rem, 2.6vw, 2.7rem);
  font-weight: 950;
  letter-spacing: -0.04em;
}

.dynamic-venue__hero p,
.dynamic-venue__summary p {
  margin: 0;
  color: rgba(224, 233, 242, 0.74);
}

.dynamic-venue__hero-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.dynamic-venue__hero-pills span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border-radius: 999px;
  color: rgba(234, 241, 251, 0.86);
  font-size: 0.78rem;
  font-weight: 800;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.025));
  border: 1px solid var(--venue-stroke);
}

.dynamic-venue__hero-pills i {
  color: var(--venue-accent);
}

.dynamic-venue__hero-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.dynamic-venue__timer,
.dynamic-venue__ghost,
.dynamic-venue__primary {
  border: 0;
  border-radius: 999px;
  font-weight: 900;
}

.dynamic-venue__timer {
  padding: 10px 14px;
  color: rgba(233, 239, 247, 0.76);
  background: rgba(255, 255, 255, 0.06);
}

.dynamic-venue__timer.is-active {
  color: #f4f7ff;
  background: rgba(142, 107, 196, 0.14);
  box-shadow: inset 0 0 0 1px rgba(220, 229, 251, 0.18);
}

.dynamic-venue__ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.dynamic-venue__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 410px);
  gap: 18px;
  align-items: start;
}

.dynamic-venue__map-card,
.dynamic-venue__summary {
  border-radius: 30px;
  overflow: hidden;
}

.dynamic-venue__map-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.035);
}

.dynamic-venue__zone-strip {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  overflow-x: auto;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018)),
    rgba(6, 9, 14, 0.72);
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
  scrollbar-width: none;
}

.dynamic-venue__zone-strip::-webkit-scrollbar {
  display: none;
}

.dynamic-venue__zone-strip button {
  flex: 0 0 auto;
  display: grid;
  gap: 2px;
  min-width: 146px;
  padding: 10px 13px;
  border: 1px solid rgba(196, 206, 228, 0.14);
  border-radius: 18px;
  color: rgba(234, 241, 251, 0.86);
  text-align: left;
  background:
    radial-gradient(circle at 18% 0%, rgba(220, 229, 251, 0.1), transparent 42%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.022));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.dynamic-venue__zone-strip button.is-active {
  color: #fff;
  border-color: rgba(220, 229, 251, 0.42);
  background:
    radial-gradient(circle at 12% 0%, rgba(244, 247, 255, 0.24), transparent 40%),
    radial-gradient(circle at 100% 100%, rgba(142, 107, 196, 0.22), transparent 42%),
    rgba(17, 24, 37, 0.88);
  box-shadow: 0 16px 30px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.14);
}

.dynamic-venue__zone-strip button:disabled {
  opacity: 0.46;
}

.dynamic-venue__zone-strip span {
  font-size: 0.78rem;
  font-weight: 950;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.dynamic-venue__zone-strip strong {
  color: var(--venue-accent);
  font-size: 0.9rem;
}

.dynamic-venue__breadcrumb button {
  border: 0;
  color: var(--venue-accent);
  font-weight: 900;
  background: transparent;
}

.dynamic-venue__breadcrumb {
  display: grid;
  gap: 3px;
}

.dynamic-venue__breadcrumb small {
  color: rgba(198, 207, 226, 0.76);
  font-size: 0.78rem;
  font-weight: 700;
}

.dynamic-venue__legend {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
  color: rgba(229, 236, 247, 0.72);
  font-size: 0.78rem;
}

.dynamic-venue__legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.dynamic-venue__legend i {
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

.dynamic-venue__legend .is-available { background: #dfe6ea; }
.dynamic-venue__legend .is-selected { background: linear-gradient(135deg, var(--venue-selection-soft), var(--venue-selection)); }
.dynamic-venue__legend .is-occupied { background: #ff1717; }

.dynamic-venue__stage {
  position: relative;
  min-height: 620px;
  overflow: hidden;
  background:
    radial-gradient(circle at 50% 43%, rgba(244, 247, 255, 0.18), transparent 18%),
    radial-gradient(circle at 52% 58%, rgba(142, 107, 196, 0.24), transparent 44%),
    radial-gradient(circle at 18% 12%, rgba(149, 168, 187, 0.16), transparent 31%),
    linear-gradient(180deg, rgba(6, 9, 14, 0.95), rgba(4, 6, 10, 0.99));
}

.dynamic-venue__stage::before {
  content: '';
  position: absolute;
  inset: 14px;
  border-radius: 24px;
  pointer-events: none;
  border: 1px solid rgba(196, 206, 228, 0.1);
  box-shadow: inset 0 0 70px rgba(220, 229, 251, 0.08), inset 0 0 160px rgba(142, 107, 196, 0.08);
}

.dynamic-venue__stage::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(115deg, transparent 0%, rgba(244, 247, 255, 0.09) 42%, transparent 58%),
    radial-gradient(circle at 50% 50%, transparent 42%, rgba(0, 0, 0, 0.18) 100%);
  opacity: 0.58;
}

.dynamic-venue__svg {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  min-height: 620px;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.dynamic-venue__stage.is-dragging .dynamic-venue__svg {
  cursor: grabbing;
}

.dynamic-venue__stage.is-pinching .dynamic-venue__zone,
.dynamic-venue__stage.is-pinching .dynamic-venue__seat {
  transition: none;
}

.dynamic-venue__stage.is-pinching .dynamic-venue__zone rect,
.dynamic-venue__stage.is-pinching .dynamic-venue__zone-aura,
.dynamic-venue__stage.is-pinching .dynamic-venue__object-ring-shadow,
.dynamic-venue__stage.is-pinching .dynamic-venue__object-ring-floor,
.dynamic-venue__stage.is-pinching .dynamic-venue__seat,
.dynamic-venue__stage.is-pinching .dynamic-venue__seat rect {
  filter: none !important;
}

.dynamic-venue__stage.is-pinching .dynamic-venue__seat-halo {
  animation: none !important;
}

.dynamic-venue__floor {
  fill: rgba(7, 10, 16, 0.96);
  stroke: rgba(202, 214, 223, 0.13);
  stroke-width: 2;
}

.dynamic-venue__floor-grid {
  fill: url(#dynamicVenueGrid);
  opacity: 0.64;
  pointer-events: none;
}

.dynamic-venue__guides {
  pointer-events: none;
}

.dynamic-venue__guides line,
.dynamic-venue__guides circle {
  fill: none;
  stroke: rgba(202, 214, 223, 0.12);
  stroke-width: 2;
  stroke-dasharray: 16 18;
}

.dynamic-venue__zone {
  cursor: pointer;
  transition: opacity 180ms ease, filter 180ms ease;
}

.dynamic-venue__zone rect:not(.dynamic-venue__zone-aura) {
  opacity: 0.72;
  stroke-width: 2.2;
  filter: drop-shadow(0 12px 32px rgba(0, 0, 0, 0.18));
}

.dynamic-venue__zone-aura {
  fill: none;
  opacity: 0.22;
  stroke-width: 5;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__zone text {
  fill: #fff;
  font-size: 28px;
  font-weight: 950;
  letter-spacing: 0.08em;
  paint-order: stroke;
  stroke: rgba(0, 0, 0, 0.55);
  stroke-width: 5px;
}

.dynamic-venue__zone .dynamic-venue__zone-price {
  font-size: 20px;
  fill: #dce5fb;
}

.dynamic-venue__zone.is-muted {
  opacity: 0.28;
}

.dynamic-venue__zone.is-active rect {
  opacity: 0.88;
  stroke-width: 4;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__zone.has-zone-selection rect:not(.dynamic-venue__zone-aura) {
  opacity: 0.92;
  stroke: var(--venue-selection-soft);
  stroke-width: 4;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__zone.has-zone-selection .dynamic-venue__zone-aura {
  opacity: 0.42;
}

.dynamic-venue__zone.is-preview rect {
  opacity: 0.9;
  stroke-width: 3.4;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__zone.is-unconfigured rect {
  opacity: 0.12;
}

.dynamic-venue__object-ring-shadow {
  fill: rgba(4, 6, 10, 0.52);
  filter: blur(12px);
}

.dynamic-venue__object-ring-floor {
  fill: url(#dynamicVenueRing);
  stroke: rgba(255, 255, 255, 0.5);
  stroke-width: 4;
  filter: drop-shadow(0 24px 52px rgba(124, 112, 214, 0.34));
}

.dynamic-venue__object-ring-inner {
  fill: rgba(255, 255, 255, 0.12);
  stroke: rgba(5, 8, 14, 0.24);
  stroke-width: 2;
}

.dynamic-venue__object-ring line {
  stroke: rgba(255, 255, 255, 0.54);
  stroke-width: 4;
  stroke-linecap: round;
}

.dynamic-venue__object-entrance {
  fill: rgba(196, 206, 228, 0.18);
  stroke: rgba(196, 206, 228, 0.28);
  stroke-width: 3;
}

.dynamic-venue__object {
  pointer-events: none;
}

.dynamic-venue__object text {
  fill: #061116;
  font-size: 32px;
  font-weight: 950;
  letter-spacing: 0.16em;
}

.dynamic-venue__seat {
  cursor: pointer;
  transition: opacity 160ms ease, transform 160ms ease, filter 160ms ease;
  filter: drop-shadow(0 7px 8px rgba(0, 0, 0, 0.28));
}

.dynamic-venue__seat rect {
  fill: url(#dynamicVenueSeatAvailable);
  stroke: rgba(4, 7, 12, 0.58);
  stroke-width: 1.2;
}

.dynamic-venue__seat .dynamic-venue__seat-hit {
  fill: transparent;
  stroke: none;
  pointer-events: all;
}

.dynamic-venue__seat:hover {
  filter: drop-shadow(0 0 12px rgba(220, 229, 251, 0.4));
}

.dynamic-venue:not(.is-section-focused) .dynamic-venue__seat:not(.is-selected) {
  opacity: 0.62;
  filter: drop-shadow(0 5px 7px rgba(0, 0, 0, 0.2));
}

.dynamic-venue:not(.is-section-focused) .dynamic-venue__seat.is-occupied {
  opacity: 0.72;
}

.dynamic-venue__seat text {
  fill: #111827;
  font-size: 11px;
  font-weight: 900;
  pointer-events: none;
}

.dynamic-venue__seat .dynamic-venue__seat-halo {
  fill: rgba(177, 157, 228, 0.2);
  stroke: rgba(248, 245, 255, 0.96);
  stroke-width: 2.8;
  filter: url(#dynamicVenueGlow);
  animation: dynamicVenueSeatPulse 1.35s ease-in-out infinite;
}

.dynamic-venue__seat.is-selected rect:not(.dynamic-venue__seat-halo):not(.dynamic-venue__seat-hit) {
  fill: url(#dynamicVenueSeatSelected);
  stroke: var(--venue-selection-soft);
  stroke-width: 3;
  filter: url(#dynamicVenueGlow);
}

.dynamic-venue__seat.is-occupied {
  cursor: not-allowed;
}

.dynamic-venue__seat.is-occupied rect:not(.dynamic-venue__seat-hit) {
  fill: #dc2f45;
  opacity: 0.72;
}

.dynamic-venue__seat.is-muted {
  opacity: 0.18;
}

.dynamic-venue__map-hint {
  position: absolute;
  left: 16px;
  bottom: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  color: #fff;
  background: rgba(5, 8, 13, 0.68);
  border: 1px solid rgba(196, 206, 228, 0.14);
  backdrop-filter: blur(14px);
}

.dynamic-venue__map-hint i {
  color: var(--venue-accent);
}

.dynamic-venue__zoom-controls {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 2;
  display: grid;
  gap: 8px;
}

.dynamic-venue__zoom-controls button {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(196, 206, 228, 0.16);
  border-radius: 15px;
  color: #eef3fa;
  background: rgba(7, 10, 16, 0.72);
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.26), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(18px) saturate(150%);
}

.dynamic-venue__zoom-controls button:hover {
  color: var(--venue-accent-strong);
  border-color: rgba(220, 229, 251, 0.38);
}

.dynamic-venue__seat-peek {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 2;
  display: grid;
  gap: 3px;
  min-width: 190px;
  padding: 14px 16px;
  border-radius: 22px;
  color: #f4f8fb;
  background:
    radial-gradient(circle at 20% 0%, rgba(220, 229, 251, 0.16), transparent 42%),
    linear-gradient(145deg, rgba(10, 14, 21, 0.88), rgba(5, 7, 12, 0.94));
  border: 1px solid rgba(220, 229, 251, 0.18);
  box-shadow: 0 22px 44px rgba(0, 0, 0, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px) saturate(150%);
}

.dynamic-venue__seat-peek small {
  color: var(--venue-accent);
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.dynamic-venue__seat-peek strong {
  font-size: 2.25rem;
  line-height: 1;
  letter-spacing: -0.06em;
}

.dynamic-venue__seat-peek span {
  color: rgba(214, 224, 248, 0.82);
  font-weight: 800;
}

.dynamic-venue__zone-picker {
  position: absolute;
  left: 18px;
  bottom: 18px;
  z-index: 3;
  display: grid;
  gap: 7px;
  min-width: 230px;
  padding: 15px;
  border-radius: 24px;
  color: #f5f7ff;
  background:
    radial-gradient(circle at 18% 0%, rgba(220, 229, 251, 0.14), transparent 36%),
    radial-gradient(circle at 100% 100%, rgba(112, 84, 184, 0.2), transparent 42%),
    linear-gradient(145deg, rgba(8, 11, 17, 0.9), rgba(4, 6, 10, 0.96));
  border: 1px solid rgba(196, 206, 228, 0.16);
  box-shadow: 0 22px 52px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(22px) saturate(150%);
}

.dynamic-venue__zone-picker small {
  color: var(--venue-selection-soft);
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.dynamic-venue__zone-picker strong {
  font-size: 1.12rem;
  letter-spacing: -0.03em;
}

.dynamic-venue__zone-picker-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.dynamic-venue__zone-picker-title span {
  display: inline-grid;
  place-items: center;
  min-width: 44px;
  height: 34px;
  padding: 0 10px;
  border-radius: 999px;
  color: #140f24;
  font-weight: 950;
  background: linear-gradient(135deg, var(--venue-selection-soft), var(--venue-selection) 62%, var(--venue-selection-deep));
  box-shadow: 0 12px 24px rgba(112, 84, 184, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4);
}

.dynamic-venue__zone-picker > span {
  color: rgba(214, 224, 248, 0.78);
  font-weight: 800;
}

.dynamic-venue__zone-stepper {
  display: grid;
  grid-template-columns: 42px 1fr 42px;
  align-items: center;
  gap: 8px;
}

.dynamic-venue__zone-stepper button {
  width: 42px;
  height: 42px;
  border: 1px solid rgba(220, 229, 251, 0.16);
  border-radius: 15px;
  color: #eef3ff;
  background: rgba(255, 255, 255, 0.06);
}

.dynamic-venue__zone-stepper b {
  display: grid;
  place-items: center;
  min-height: 42px;
  border-radius: 15px;
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(220, 229, 251, 0.12);
}

.dynamic-venue__summary {
  position: sticky;
  top: 16px;
  display: grid;
  gap: 14px;
  padding: 18px;
}

.dynamic-venue__summary-head {
  display: grid;
  gap: 6px;
}

.dynamic-venue__summary-head strong {
  font-size: 1.35rem;
}

.dynamic-venue__selected-list {
  display: grid;
  gap: 10px;
  max-height: 280px;
  overflow: auto;
}

.dynamic-venue__selected-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  padding: 13px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 10% 0%, rgba(220, 229, 251, 0.14), transparent 38%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.075), rgba(255, 255, 255, 0.025));
  border: 1px solid rgba(220, 229, 251, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.dynamic-venue__selected-copy {
  display: grid;
  gap: 3px;
}

.dynamic-venue__selected-copy em {
  color: rgba(198, 207, 226, 0.72);
  font-size: 0.74rem;
  font-style: normal;
  font-weight: 700;
}

.dynamic-venue__selected-mark {
  display: grid;
  place-items: center;
  width: 58px;
  height: 58px;
  border-radius: 18px;
  color: #140f24;
  background: linear-gradient(135deg, var(--venue-selection-soft), var(--venue-selection) 56%, var(--venue-selection-deep));
  box-shadow: 0 16px 28px rgba(142, 107, 196, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.42);
}

.dynamic-venue__selected-mark span {
  font-weight: 950;
  letter-spacing: -0.08em;
}

.dynamic-venue__selected-item button {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  background: rgba(220, 47, 69, 0.76);
}

.dynamic-venue__empty-box,
.dynamic-venue__loading,
.dynamic-venue__error {
  display: grid;
  place-items: center;
  gap: 8px;
  min-height: 220px;
  padding: 24px;
  text-align: center;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.04);
}

.dynamic-venue__empty-box i {
  font-size: 2rem;
  color: var(--venue-accent);
}

.dynamic-venue__totals {
  display: grid;
  gap: 10px;
  padding: 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
}

.dynamic-venue__totals div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.dynamic-venue__totals span {
  color: rgba(225, 232, 242, 0.7);
}

.dynamic-venue__totals .is-total {
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 1.1rem;
}

.dynamic-venue__primary {
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 15px 18px;
  color: #eef3fa;
  background:
    radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.12), transparent 36%),
    linear-gradient(180deg, rgba(24, 39, 48, 0.34), rgba(11, 11, 17, 0.72));
  border: 1px solid rgba(196, 206, 228, 0.16);
  box-shadow: 0 18px 34px rgba(2, 4, 10, 0.3), 0 10px 18px rgba(126, 145, 184, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.dynamic-venue__primary::before {
  content: '';
  position: absolute;
  inset: -12px;
  pointer-events: none;
  background:
    radial-gradient(circle at 16% 10%, rgba(228, 232, 239, 0.14), transparent 28%),
    radial-gradient(circle at 82% 84%, rgba(142, 107, 196, 0.18), transparent 34%);
  filter: blur(16px);
  opacity: 0.22;
}

.dynamic-venue__mobile-cta {
  display: none;
}

.dynamic-venue__primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dynamic-venue__hold-error {
  margin: 0;
  padding: 10px 12px;
  border-radius: 14px;
  color: #ffd8d2;
  background: rgba(255, 40, 40, 0.12);
}

.dynamic-venue__hold-toast {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  max-width: calc(100% - 36px);
  margin: 0;
  padding: 11px 14px;
  border-radius: 18px;
  color: #ffe4df;
  background: rgba(49, 10, 16, 0.82);
  border: 1px solid rgba(255, 116, 116, 0.24);
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.34);
  backdrop-filter: blur(18px) saturate(150%);
}

.dynamic-venue__loader-ring {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.12);
  border-top-color: var(--venue-accent);
  animation: dynamicVenueSpin 860ms linear infinite;
}

@keyframes dynamicVenueSpin {
  to { transform: rotate(360deg); }
}

@keyframes dynamicVenueSeatPulse {
  0%, 100% { opacity: 0.58; stroke-width: 2.3; }
  50% { opacity: 1; stroke-width: 3.8; }
}

@media (max-width: 1024px) {
  .dynamic-venue__grid {
    grid-template-columns: 1fr;
  }

  .dynamic-venue__summary {
    position: sticky;
    bottom: 12px;
    top: auto;
    z-index: 4;
  }
}

@media (max-width: 720px) {
  .dynamic-venue {
    gap: 8px;
    margin-inline: -10px;
  }

  .dynamic-venue.has-selection {
    padding-bottom: 92px;
  }

  .dynamic-venue.has-zone-picker {
    padding-bottom: 154px;
  }

  .dynamic-venue.has-zone-picker.has-selection {
    padding-bottom: 224px;
  }

  .dynamic-venue__map-card {
    border-radius: 26px;
    box-shadow: 0 28px 80px rgba(0, 0, 0, 0.46);
  }

  .dynamic-venue__map-toolbar {
    align-items: flex-start;
    flex-direction: column;
    padding: 12px 13px 10px;
    gap: 6px;
  }

  .dynamic-venue__breadcrumb {
    width: 100%;
  }

  .dynamic-venue__breadcrumb button {
    padding: 0;
    font-size: 0.82rem;
  }

  .dynamic-venue__breadcrumb span {
    color: #fff;
    font-weight: 900;
  }

  .dynamic-venue__breadcrumb small {
    max-width: 320px;
    font-size: 0.74rem;
    line-height: 1.25;
  }

  .dynamic-venue__zone-strip {
    gap: 8px;
    padding: 10px 12px;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
  }

  .dynamic-venue__zone-strip button {
    min-width: 132px;
    padding: 9px 11px;
    border-radius: 16px;
    scroll-snap-align: start;
  }

  .dynamic-venue__zone-strip span {
    font-size: 0.7rem;
  }

  .dynamic-venue__zone-strip strong {
    font-size: 0.82rem;
  }

  .dynamic-venue__stage,
  .dynamic-venue__svg {
    min-height: clamp(440px, 64svh, 610px);
    height: clamp(440px, 64svh, 610px);
  }

  .dynamic-venue__legend {
    display: none;
  }

  .dynamic-venue__seat-peek {
    display: none;
  }

  .dynamic-venue__zone-picker {
    position: fixed;
    left: max(12px, env(safe-area-inset-left));
    right: max(12px, env(safe-area-inset-right));
    bottom: max(14px, calc(env(safe-area-inset-bottom) + 14px));
    z-index: 1250;
    gap: 10px;
    min-width: 0;
    padding: 16px;
    border-radius: 28px;
    box-shadow: 0 26px 66px rgba(0, 0, 0, 0.56), 0 0 36px rgba(142, 107, 196, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  .dynamic-venue.has-selection .dynamic-venue__zone-picker {
    bottom: max(92px, calc(env(safe-area-inset-bottom) + 92px));
  }

  .dynamic-venue__zone-picker small {
    font-size: 0.72rem;
  }

  .dynamic-venue__zone-picker strong {
    font-size: 1.22rem;
  }

  .dynamic-venue__zone-stepper {
    grid-template-columns: 48px 1fr 48px;
  }

  .dynamic-venue__zone-stepper button,
  .dynamic-venue__zone-stepper b {
    height: 48px;
    border-radius: 17px;
  }

  .dynamic-venue__zoom-controls {
    top: 12px;
    right: 12px;
  }

  .dynamic-venue__zoom-controls button {
    width: 38px;
    height: 38px;
    border-radius: 14px;
  }

  .dynamic-venue__selected-item {
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  .dynamic-venue__selected-item > span {
    grid-column: 2 / 4;
  }

  .dynamic-venue__map-hint {
    left: 12px;
    right: 12px;
    bottom: 14px;
    align-items: flex-start;
    border-radius: 20px;
    font-size: 0.78rem;
    line-height: 1.25;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.34);
  }

  .dynamic-venue__hold-toast {
    left: 12px;
    right: 12px;
    bottom: 78px;
    max-width: none;
    width: auto;
  }

  .dynamic-venue__mobile-cta {
    position: fixed;
    left: max(12px, env(safe-area-inset-left));
    right: max(12px, env(safe-area-inset-right));
    bottom: max(10px, env(safe-area-inset-bottom));
    z-index: 1200;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    padding: 10px 10px 10px 14px;
    border-radius: 24px;
    background:
      radial-gradient(circle at 14% 0%, rgba(221, 229, 247, 0.12), transparent 38%),
      radial-gradient(circle at 100% 100%, rgba(142, 107, 196, 0.18), transparent 44%),
      linear-gradient(145deg, rgba(9, 13, 19, 0.94), rgba(4, 6, 10, 0.98));
    border: 1px solid rgba(196, 206, 228, 0.16);
    box-shadow: 0 22px 54px rgba(0, 0, 0, 0.48), 0 0 28px rgba(142, 107, 196, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(22px) saturate(150%);
  }

  .dynamic-venue__mobile-cta small,
  .dynamic-venue__mobile-cta strong {
    display: block;
  }

  .dynamic-venue__mobile-cta small {
    color: rgba(198, 207, 226, 0.78);
    font-size: 0.76rem;
    font-weight: 800;
  }

  .dynamic-venue__mobile-cta .dynamic-venue__mobile-tax {
    margin-top: 2px;
    color: rgba(222, 230, 244, 0.72);
    font-size: 0.68rem;
    font-weight: 700;
  }

  .dynamic-venue__mobile-cta strong {
    color: #edf3ff;
    font-size: 1.12rem;
    letter-spacing: -0.04em;
  }

  .dynamic-venue__mobile-cta button {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 48px;
    padding: 0 10px 0 18px;
    border: 0;
    border-radius: 999px;
    color: #eef3ff;
    font-weight: 900;
    letter-spacing: -0.02em;
    background: rgba(10, 10, 14, 0.18);
    box-shadow: 0 18px 34px rgba(2, 4, 10, 0.28), 0 10px 18px rgba(83, 60, 120, 0.08);
    backdrop-filter: blur(24px) saturate(135%);
  }

  .dynamic-venue__mobile-cta button::before,
  .dynamic-venue__mobile-cta button::after {
    content: '';
    position: absolute;
    border-radius: inherit;
    pointer-events: none;
  }

  .dynamic-venue__mobile-cta button::before {
    inset: 0;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0.06) 18%, rgba(255, 255, 255, 0.02) 48%, rgba(126, 104, 162, 0.04) 100%);
    opacity: 0.32;
  }

  .dynamic-venue__mobile-cta button::after {
    inset: 1px;
    background:
      radial-gradient(circle at 24% -12%, rgba(255, 255, 255, 0.18), transparent 34%),
      radial-gradient(circle at 84% 50%, rgba(120, 91, 163, 0.12), transparent 24%),
      linear-gradient(180deg, rgba(34, 36, 46, 0.32), rgba(10, 10, 16, 0.56));
    opacity: 0.82;
  }

  .dynamic-venue__mobile-cta button span,
  .dynamic-venue__mobile-cta button i {
    position: relative;
    z-index: 1;
  }

  .dynamic-venue__mobile-cta button i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    color: #f4f7fb;
    background:
      radial-gradient(circle at 30% 24%, rgba(255, 255, 255, 0.24), transparent 34%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.12), rgba(142, 107, 196, 0.18));
    border: 1px solid rgba(255, 255, 255, 0.14);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 8px 18px rgba(4, 6, 12, 0.16);
  }

  .dynamic-venue__summary {
    display: none;
  }
}

@media (max-width: 380px) {
  .dynamic-venue__stage,
  .dynamic-venue__svg {
    min-height: clamp(420px, 62svh, 500px);
    height: clamp(420px, 62svh, 500px);
  }

  .dynamic-venue__mobile-cta {
    grid-template-columns: 1fr;
  }

  .dynamic-venue__mobile-cta button {
    width: 100%;
  }

  .dynamic-venue.has-zone-picker.has-selection {
    padding-bottom: 284px;
  }

  .dynamic-venue.has-selection .dynamic-venue__zone-picker {
    bottom: max(146px, calc(env(safe-area-inset-bottom) + 146px));
  }
}
</style>
